import React, { useState, useEffect, useRef } from 'react';
import { 
  getCompanySettings, 
  updateCompanySettings, 
  uploadCompanyLogo, 
  deleteCompanyLogo,
  getCompanyLogoUrl,
  getSettings,
  updateSettings,
  getVerifactuStatus,
  generateVerifactuHashes,
  getPortalSettings,
  updatePortalSettings
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Building2, 
  Upload, 
  Trash2, 
  Save,
  Image as ImageIcon,
  Hash,
  Settings as SettingsIcon,
  CreditCard,
  Palette,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertCircle,
  QrCode,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';

export const SettingsPage = () => {
  const [company, setCompany] = useState({
    company_name: '',
    tax_id: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    bank_account: '',
    bank_name: '',
    invoice_footer: '',
    invoice_color: '#FF6B4A',
    internal_hourly_cost: '',
    default_hourly_rate: ''
  });
  const [numbering, setNumbering] = useState({
    next_invoice_number: 1,
    next_quote_number: 1,
    next_delivery_number: 1,
    invoice_prefix: 'FAC-',
    quote_prefix: 'PRE-',
    delivery_prefix: 'ALB-'
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [verifactu, setVerifactu] = useState(null);
  const [generatingHashes, setGeneratingHashes] = useState(false);
  const [portalSettings, setPortalSettings] = useState({
    primary_color: '#FF6B4A',
    welcome_message: '',
    show_quotes: true,
    show_contracts: false,
    show_messages: true,
  });
  const [savingPortal, setSavingPortal] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
    loadVerifactu();
    loadPortal();
  }, []);

  const loadPortal = async () => {
    try {
      const { data } = await getPortalSettings();
      setPortalSettings({
        primary_color: data.primary_color || '#FF6B4A',
        welcome_message: data.welcome_message || '',
        show_quotes: data.show_quotes !== false,
        show_contracts: data.show_contracts || false,
        show_messages: data.show_messages !== false,
      });
    } catch (e) { /* silent */ }
  };

  const handleSavePortal = async () => {
    setSavingPortal(true);
    try {
      await updatePortalSettings(portalSettings);
      toast.success('Configuración del portal guardada');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSavingPortal(false);
    }
  };

  const loadVerifactu = async () => {
    try {
      const { data } = await getVerifactuStatus();
      setVerifactu(data);
    } catch (e) { /* silently fail */ }
  };

  const handleGenerateHashes = async () => {
    setGeneratingHashes(true);
    try {
      const { data } = await generateVerifactuHashes();
      toast.success(`${data.hashed} facturas procesadas con hash Verifactu`);
      loadVerifactu();
    } catch { toast.error('Error generando hashes'); }
    finally { setGeneratingHashes(false); }
  };

  const loadSettings = async () => {
    try {
      const [companyRes, numberingRes] = await Promise.all([
        getCompanySettings(),
        getSettings()
      ]);
      
      setCompany({
        company_name: companyRes.data.company_name || '',
        tax_id: companyRes.data.tax_id || '',
        address: companyRes.data.address || '',
        city: companyRes.data.city || '',
        postal_code: companyRes.data.postal_code || '',
        phone: companyRes.data.phone || '',
        email: companyRes.data.email || '',
        website: companyRes.data.website || '',
        bank_account: companyRes.data.bank_account || '',
        bank_name: companyRes.data.bank_name || '',
        invoice_footer: companyRes.data.invoice_footer || '',
        invoice_color: companyRes.data.invoice_color || '#FF6B4A',
        internal_hourly_cost: companyRes.data.internal_hourly_cost ?? '',
        default_hourly_rate: companyRes.data.default_hourly_rate ?? ''
      });
      
      if (companyRes.data.logo_path) {
        setHasLogo(true);
        setLogoPreview(getCompanyLogoUrl() + '?t=' + Date.now());
      }
      
      if (numberingRes.data) {
        setNumbering({
          next_invoice_number: numberingRes.data.next_invoice_number || 1,
          next_quote_number: numberingRes.data.next_quote_number || 1,
          next_delivery_number: numberingRes.data.next_delivery_number || 1,
          invoice_prefix: numberingRes.data.invoice_prefix || 'FAC-',
          quote_prefix: numberingRes.data.quote_prefix || 'PRE-',
          delivery_prefix: numberingRes.data.delivery_prefix || 'ALB-'
        });
      }
    } catch (e) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      const payload = {
        ...company,
        internal_hourly_cost: company.internal_hourly_cost === '' ? null : parseFloat(company.internal_hourly_cost),
        default_hourly_rate: company.default_hourly_rate === '' ? null : parseFloat(company.default_hourly_rate),
      };
      await updateCompanySettings(payload);
      toast.success('Datos de empresa guardados');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNumbering = async () => {
    setSaving(true);
    try {
      await updateSettings(numbering);
      toast.success('Numeración guardada');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecciona una imagen');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no puede superar 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      await uploadCompanyLogo(file);
      setHasLogo(true);
      setLogoPreview(getCompanyLogoUrl() + '?t=' + Date.now());
      toast.success('Logo subido correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Eliminar el logo?')) return;
    
    try {
      await deleteCompanyLogo();
      setHasLogo(false);
      setLogoPreview(null);
      toast.success('Logo eliminado');
    } catch (e) {
      toast.error('Error al eliminar logo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Configuración</h1>
        <p className="text-stone-500 text-sm mt-1">Personaliza tu empresa y plantillas</p>
      </div>

      {/* Company Settings */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#FF6B4A]" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold">Datos de Empresa</h2>
            <p className="text-sm text-stone-500">Estos datos aparecerán en tus facturas</p>
          </div>
        </div>

        {/* Logo Upload */}
        <div className="mb-6 pb-6 border-b border-stone-100">
          <Label className="block mb-3">Logo de Empresa</Label>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-24 h-24 bg-stone-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-stone-300">
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt="Logo" 
                  className="w-full h-full object-contain"
                  onError={() => setLogoPreview(null)}
                />
              ) : (
                <ImageIcon className="w-8 h-8 text-stone-400" />
              )}
            </div>
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="rounded-xl"
                data-testid="upload-logo-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
              </Button>
              {hasLogo && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDeleteLogo}
                  className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-logo-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              )}
              <p className="text-xs text-stone-500">JPG, PNG, GIF o WebP. Máximo 2MB.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="company_name">Nombre de Empresa</Label>
            <Input
              id="company_name"
              value={company.company_name}
              onChange={(e) => setCompany({ ...company, company_name: e.target.value })}
              className="rounded-xl"
              placeholder="Mi Empresa S.L."
              data-testid="company-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax_id">NIF/CIF</Label>
            <Input
              id="tax_id"
              value={company.tax_id}
              onChange={(e) => setCompany({ ...company, tax_id: e.target.value })}
              className="rounded-xl font-mono"
              placeholder="B12345678"
              data-testid="company-taxid-input"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              value={company.address}
              onChange={(e) => setCompany({ ...company, address: e.target.value })}
              className="rounded-xl"
              placeholder="Calle Principal 123"
              data-testid="company-address-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="city">Ciudad</Label>
            <Input
              id="city"
              value={company.city}
              onChange={(e) => setCompany({ ...company, city: e.target.value })}
              className="rounded-xl"
              placeholder="Madrid"
              data-testid="company-city-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="postal_code">Código Postal</Label>
            <Input
              id="postal_code"
              value={company.postal_code}
              onChange={(e) => setCompany({ ...company, postal_code: e.target.value })}
              className="rounded-xl"
              placeholder="28001"
              data-testid="company-postal-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              value={company.phone}
              onChange={(e) => setCompany({ ...company, phone: e.target.value })}
              className="rounded-xl"
              placeholder="+34 912 345 678"
              data-testid="company-phone-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={company.email}
              onChange={(e) => setCompany({ ...company, email: e.target.value })}
              className="rounded-xl"
              placeholder="info@miempresa.com"
              data-testid="company-email-input"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="website">Sitio Web</Label>
            <Input
              id="website"
              value={company.website}
              onChange={(e) => setCompany({ ...company, website: e.target.value })}
              className="rounded-xl"
              placeholder="https://www.miempresa.com"
              data-testid="company-website-input"
            />
          </div>
        </div>

        {/* Bank Account Section */}
        <div className="mt-6 pt-6 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-medium text-sm">Datos Bancarios</h3>
              <p className="text-xs text-stone-500">Se mostrarán en tus facturas y presupuestos</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Nombre del Banco</Label>
              <Input
                id="bank_name"
                value={company.bank_name}
                onChange={(e) => setCompany({ ...company, bank_name: e.target.value })}
                className="rounded-xl"
                placeholder="CaixaBank, BBVA, Santander..."
                data-testid="company-bank-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bank_account">IBAN / Cuenta Bancaria</Label>
              <Input
                id="bank_account"
                value={company.bank_account}
                onChange={(e) => setCompany({ ...company, bank_account: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="ES12 3456 7890 1234 5678 9012"
                data-testid="company-bank-account-input"
              />
            </div>
          </div>
        </div>

        {/* Time Tracking / Profitability */}
        <div className="mt-6 pt-6 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Palette className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-medium text-sm">Time Tracking y Rentabilidad</h3>
              <p className="text-xs text-stone-500">Tarifas y costes para calcular el margen de proyectos</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="default_hourly_rate">Tarifa por hora (€/h)</Label>
              <Input
                id="default_hourly_rate"
                type="number"
                step="0.01"
                min="0"
                value={company.default_hourly_rate}
                onChange={(e) => setCompany({ ...company, default_hourly_rate: e.target.value })}
                className="rounded-xl"
                placeholder="60"
                data-testid="company-default-hourly-rate-input"
              />
              <p className="text-xs text-stone-500">Precio por defecto que cobras al cliente por hora</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="internal_hourly_cost">Coste interno (€/h)</Label>
              <Input
                id="internal_hourly_cost"
                type="number"
                step="0.01"
                min="0"
                value={company.internal_hourly_cost}
                onChange={(e) => setCompany({ ...company, internal_hourly_cost: e.target.value })}
                className="rounded-xl"
                placeholder="25"
                data-testid="company-internal-hourly-cost-input"
              />
              <p className="text-xs text-stone-500">Tu coste por hora trabajada (sueldo + gastos). Usado para calcular margen real.</p>
            </div>
          </div>
        </div>

        {/* Invoice Customization */}
        <div className="mt-6 pt-6 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-violet-50 rounded-xl flex items-center justify-center">
              <Palette className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <h3 className="font-medium text-sm">Personalización de Facturas</h3>
              <p className="text-xs text-stone-500">Color y pie de página de tus documentos</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoice_color">Color de Marca</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="invoice_color"
                  value={company.invoice_color}
                  onChange={(e) => setCompany({ ...company, invoice_color: e.target.value })}
                  className="w-10 h-10 rounded-xl cursor-pointer border border-stone-100"
                  data-testid="company-invoice-color-input"
                />
                <Input
                  value={company.invoice_color}
                  onChange={(e) => setCompany({ ...company, invoice_color: e.target.value })}
                  className="rounded-xl font-mono flex-1"
                  placeholder="#FF6B4A"
                />
                <div className="w-24 h-10 rounded-xl" style={{ backgroundColor: company.invoice_color }}></div>
              </div>
              <p className="text-xs text-stone-500">Color del título y cabecera de tabla en PDFs</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice_footer">Pie de Página</Label>
              <Textarea
                id="invoice_footer"
                value={company.invoice_footer}
                onChange={(e) => setCompany({ ...company, invoice_footer: e.target.value })}
                className="rounded-xl"
                placeholder="Inscrita en el Registro Mercantil de Madrid..."
                rows={3}
                data-testid="company-invoice-footer-input"
              />
              <p className="text-xs text-stone-500">Texto legal o información adicional al final de cada factura</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSaveCompany}
            disabled={saving}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="save-company-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar Datos'}
          </Button>
        </div>
      </div>

      {/* Numbering Settings */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
            <Hash className="w-5 h-5 text-[#FF6B4A]" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold">Numeración de Documentos</h2>
            <p className="text-sm text-stone-500">Configura los prefijos y siguientes números</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Invoices */}
          <div className="space-y-4 p-4 bg-stone-50 rounded-xl">
            <h3 className="font-medium text-stone-900">Facturas</h3>
            <div className="space-y-2">
              <Label htmlFor="invoice_prefix">Prefijo</Label>
              <Input
                id="invoice_prefix"
                value={numbering.invoice_prefix}
                onChange={(e) => setNumbering({ ...numbering, invoice_prefix: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="FAC-"
                data-testid="invoice-prefix-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next_invoice_number">Siguiente Número</Label>
              <Input
                id="next_invoice_number"
                type="number"
                min="1"
                value={numbering.next_invoice_number}
                onChange={(e) => setNumbering({ ...numbering, next_invoice_number: parseInt(e.target.value) || 1 })}
                className="rounded-xl font-mono"
                data-testid="invoice-number-input"
              />
            </div>
            <p className="text-xs text-stone-500">
              Próxima: <span className="font-mono">{numbering.invoice_prefix}{String(numbering.next_invoice_number).padStart(5, '0')}</span>
            </p>
          </div>

          {/* Quotes */}
          <div className="space-y-4 p-4 bg-stone-50 rounded-xl">
            <h3 className="font-medium text-stone-900">Presupuestos</h3>
            <div className="space-y-2">
              <Label htmlFor="quote_prefix">Prefijo</Label>
              <Input
                id="quote_prefix"
                value={numbering.quote_prefix}
                onChange={(e) => setNumbering({ ...numbering, quote_prefix: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="PRE-"
                data-testid="quote-prefix-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next_quote_number">Siguiente Número</Label>
              <Input
                id="next_quote_number"
                type="number"
                min="1"
                value={numbering.next_quote_number}
                onChange={(e) => setNumbering({ ...numbering, next_quote_number: parseInt(e.target.value) || 1 })}
                className="rounded-xl font-mono"
                data-testid="quote-number-input"
              />
            </div>
            <p className="text-xs text-stone-500">
              Próximo: <span className="font-mono">{numbering.quote_prefix}{String(numbering.next_quote_number).padStart(5, '0')}</span>
            </p>
          </div>

          {/* Delivery Notes */}
          <div className="space-y-4 p-4 bg-stone-50 rounded-xl">
            <h3 className="font-medium text-stone-900">Albaranes</h3>
            <div className="space-y-2">
              <Label htmlFor="delivery_prefix">Prefijo</Label>
              <Input
                id="delivery_prefix"
                value={numbering.delivery_prefix}
                onChange={(e) => setNumbering({ ...numbering, delivery_prefix: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="ALB-"
                data-testid="delivery-prefix-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next_delivery_number">Siguiente Número</Label>
              <Input
                id="next_delivery_number"
                type="number"
                min="1"
                value={numbering.next_delivery_number}
                onChange={(e) => setNumbering({ ...numbering, next_delivery_number: parseInt(e.target.value) || 1 })}
                className="rounded-xl font-mono"
                data-testid="delivery-number-input"
              />
            </div>
            <p className="text-xs text-stone-500">
              Próximo: <span className="font-mono">{numbering.delivery_prefix}{String(numbering.next_delivery_number).padStart(5, '0')}</span>
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSaveNumbering}
            disabled={saving}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="save-numbering-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar Numeración'}
          </Button>
        </div>
      </div>

      {/* Verifactu Section */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-2xl flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-bold">Verifactu</h2>
                <p className="text-sm text-stone-400">Sistema de verificación de facturas de la AEAT</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="verifactu-toggle">
              <input type="checkbox" checked={verifactu?.user_enabled || false} onChange={async (e) => {
                try {
                  await updateCompanySettings({ verifactu_enabled: e.target.checked });
                  setVerifactu(prev => ({ ...prev, user_enabled: e.target.checked }));
                  toast.success(e.target.checked ? 'Verifactu activado' : 'Verifactu desactivado');
                } catch { toast.error('Error al cambiar estado'); }
              }} className="sr-only peer" />
              <div className="w-11 h-6 bg-stone-200 rounded-full peer peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
            </label>
          </div>

        {verifactu && !verifactu.user_enabled && (
          <div className="mt-4 bg-stone-50 border border-stone-100 rounded-xl p-4">
            <p className="text-sm text-stone-500">
              Verifactu está <strong>desactivado</strong>. Actívalo cuando quieras comenzar a generar hashes SHA-256 encadenados y códigos QR en tus facturas para cumplir con la normativa de la AEAT.
            </p>
          </div>
        )}

        {verifactu && verifactu.user_enabled && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-stone-900">{verifactu.total_invoices}</p>
                <p className="text-xs text-stone-400 mt-1">Total Facturas</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{verifactu.hashed_invoices}</p>
                <p className="text-xs text-stone-400 mt-1">Con Hash</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{verifactu.pending}</p>
                <p className="text-xs text-stone-400 mt-1">Pendientes</p>
              </div>
              <div className="rounded-xl p-4 text-center border border-stone-100">
                <div className="flex items-center justify-center gap-1.5">
                  {verifactu.company_tax_id_configured
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    : <AlertCircle className="w-5 h-5 text-amber-500" />}
                </div>
                <p className="text-xs text-stone-400 mt-1">NIF/CIF</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <QrCode className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Modo Preparación</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Verifactu está en modo sandbox. Los hashes SHA-256 se generan y encadenan localmente.
                    Para activar el envío a la AEAT, necesitas configurar tu certificado digital.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleGenerateHashes}
                disabled={generatingHashes}
                className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
                data-testid="generate-hashes-button"
              >
                {generatingHashes ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Hash className="w-4 h-4 mr-2" />}
                {generatingHashes ? 'Generando...' : 'Generar Hashes'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Portal del Cliente */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6" data-testid="portal-settings-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-50 rounded-2xl flex items-center justify-center">
            <LinkIcon className="w-5 h-5 text-[#FF6B4A]" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold">Portal del Cliente</h2>
            <p className="text-sm text-stone-400">Personaliza el portal que verán tus clientes (white-label ligero)</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">Color principal</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="color"
                  value={portalSettings.primary_color}
                  onChange={(e) => setPortalSettings({ ...portalSettings, primary_color: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                  data-testid="portal-color-input"
                />
                <Input
                  value={portalSettings.primary_color}
                  onChange={(e) => setPortalSettings({ ...portalSettings, primary_color: e.target.value })}
                  className="flex-1 font-mono"
                  placeholder="#FF6B4A"
                />
              </div>
            </div>
            <div className="flex items-end">
              <div
                className="w-full h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                style={{ background: portalSettings.primary_color }}
              >
                Vista previa de color
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">Mensaje de bienvenida</Label>
            <Textarea
              value={portalSettings.welcome_message}
              onChange={(e) => setPortalSettings({ ...portalSettings, welcome_message: e.target.value })}
              placeholder="Texto que verán los clientes al entrar al portal (opcional)"
              rows={3}
              maxLength={500}
              data-testid="portal-welcome-input"
            />
            <p className="text-xs text-stone-400 mt-1">{portalSettings.welcome_message.length}/500</p>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-2 block">Secciones visibles para el cliente</Label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100">
                <input
                  type="checkbox"
                  checked={portalSettings.show_quotes}
                  onChange={(e) => setPortalSettings({ ...portalSettings, show_quotes: e.target.checked })}
                  data-testid="portal-show-quotes"
                />
                <div>
                  <p className="text-sm font-medium text-stone-800">Presupuestos</p>
                  <p className="text-xs text-stone-500">Permitir a los clientes ver y responder presupuestos</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100">
                <input
                  type="checkbox"
                  checked={portalSettings.show_messages}
                  onChange={(e) => setPortalSettings({ ...portalSettings, show_messages: e.target.checked })}
                  data-testid="portal-show-messages"
                />
                <div>
                  <p className="text-sm font-medium text-stone-800">Mensajes</p>
                  <p className="text-xs text-stone-500">Habilitar chat bidireccional con el cliente</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 opacity-60">
                <input
                  type="checkbox"
                  checked={portalSettings.show_contracts}
                  onChange={(e) => setPortalSettings({ ...portalSettings, show_contracts: e.target.checked })}
                  disabled
                />
                <div>
                  <p className="text-sm font-medium text-stone-800">Contratos <span className="text-[10px] bg-stone-200 px-1.5 py-0.5 rounded">Próximamente</span></p>
                  <p className="text-xs text-stone-500">Ver y firmar contratos electrónicamente</p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSavePortal}
              disabled={savingPortal}
              className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full"
              data-testid="save-portal-settings-btn"
            >
              {savingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar configuración
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
